import { useState, useEffect, useRef, ReactNode } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { SEO, getOrganizationSchema, getProductSchema, getFAQSchema } from "../components/SEO";
import { CountUp } from "../components/CountUp";
import {
  BarChart3,
  FileText,
  LayoutDashboard,
  ShoppingCart,
  Receipt,
  Wallet,
  Upload,
  Wrench,
  Plug,
  ClipboardList,
  TrendingUp,
  Check,
  ChevronDown,
  ArrowRight,
  Menu,
  X,
  MessageSquare,
  Shield,
  Zap,
  Clock,
  User,
  Users,
  Award,
  Star,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger, SplitText);

function ScrollReveal({
  children,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!containerRef.current) return;
    gsap.fromTo(
      containerRef.current,
      { opacity: 0, y: 32 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: delay / 1000,
        ease: "power3.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 88%",
          toggleActions: "play none none none",
        },
      }
    );
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className={className} style={{ opacity: 0 }}>
      {children}
    </div>
  );
}

function SectionHeading({ children, className = "", as: Tag = "h2" }: { children: string; className?: string; as?: "h2" | "h3" }) {
  const ref = useRef<HTMLHeadingElement>(null);

  useGSAP(() => {
    if (!ref.current) return;
    const split = SplitText.create(ref.current, {
      type: "chars",
      tag: "span",
      smartWrap: true,
    });
    gsap.set(split.chars, { display: "inline-block" });
    gsap.fromTo(
      split.chars,
      { autoAlpha: 0, y: 40 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.49,
        stagger: { each: 0.0504, from: "start" },
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 88%",
          toggleActions: "play none none none",
        },
      }
    );
    return () => split.revert();
  }, { scope: ref });

  return (
    <Tag ref={ref} className={`section-title ${className}`}>
      {children}
    </Tag>
  );
}

const features = [
  {
    title: "Dashboard",
    description: "Visualize os principais indicadores da operação em um só lugar.",
    icon: LayoutDashboard,
  },
  {
    title: "Cadastros completos",
    description: "Gerencie clientes, fornecedores, transportadoras e produtos.",
    icon: ClipboardList,
  },
  {
    title: "Orçamentos",
    description: "Crie, acompanhe e converta orçamentos com agilidade.",
    icon: FileText,
  },
  {
    title: "Pedidos de venda",
    description: "Controle pedidos, etapas comerciais e o fluxo de vendas.",
    icon: ShoppingCart,
  },
  {
    title: "NFe e NFCe",
    description: "Emita documentos fiscais eletrônicos de forma integrada.",
    icon: Receipt,
  },
  {
    title: "Boletos e remessas",
    description: "Organize cobranças, remessas e recebimentos financeiros.",
    icon: Wallet,
  },
  {
    title: "Relatórios gerenciais",
    description: "Acompanhe resultados e tome decisões com dados confiáveis.",
    icon: BarChart3,
  },
  {
    title: "Fluxo de caixa",
    description: "Tenha clareza sobre entradas, saídas e saldo da empresa.",
    icon: TrendingUp,
  },
  {
    title: "Contas a pagar e receber",
    description: "Controle compromissos financeiros e mantenha tudo em dia.",
    icon: ClipboardList,
  },
  {
    title: "Importação de XML",
    description: "Agilize a entrada de documentos fiscais recebidos.",
    icon: Upload,
  },
  {
    title: "Ordem de serviço",
    description: "Organize serviços, atendimentos e equipes em campo.",
    icon: Wrench,
  },
  {
    title: "Integrações e WhatsApp",
    description: "Conecte canais e envie NFe, NFCe e mensagens aos clientes.",
    icon: Plug,
  },
];

const benefits = [
  { icon: Shield, text: "Redução de custos operacionais de até 25%" },
  { icon: Zap, text: "Aumento da produtividade da equipe" },
  { icon: Users, text: "Integração com sistemas existentes" },
  { icon: Clock, text: "Suporte 24/7 dedicado" },
  { icon: Award, text: "Treinamento personalizado" },
];

const testimonials = [
  {
    name: "Maria Silva",
    role: "CEO",
    company: "TechSolutions",
    content: "O ZWeb transformou nossa operação. Reduzimos custos em 30% e aumentamos a produtividade em 40%.",
    rating: 5,
  },
  {
    name: "João Santos",
    role: "Diretor de TI",
    company: "InnovateTech",
    content: "A implantação foi rápida e o suporte excepcional. Recomendo para qualquer empresa que queira crescer.",
    rating: 5,
  },
  {
    name: "Ana Oliveira",
    role: "Gerente de Operações",
    company: "GrowthPartners",
    content: "A equipe ganhou visibilidade da operação e conseguiu crescer sem aumentar a complexidade do dia a dia.",
    rating: 5,
  },
];

const faqs = [
  {
    question: "Quanto tempo leva a implantação?",
    answer: "A implantação leva de 2 a 8 semanas, dependendo do tamanho da sua empresa e da complexidade dos processos.",
  },
  {
    question: "O ZWeb integra com outros sistemas?",
    answer: "Sim! O ZWeb possui uma API completa que permite integração com os principais sistemas do mercado.",
  },
  {
    question: "Qual o custo mensal do ZWeb?",
    answer: "O custo varia de R$ 129,80 a R$ 218,90 por mês, dependendo do plano escolhido e do número de usuários.",
  },
  {
    question: "O ZWeb oferece suporte técnico?",
    answer: "Sim! Oferecemos suporte por e-mail, chat, telefone e WhatsApp. Planos premium têm suporte 24/7.",
  },
  {
    question: "Posso testar o ZWeb antes de contratar?",
    answer: "Sim! Oferecemos uma demonstração gratuita de 14 dias para você testar todas as funcionalidades.",
  },
];

const stats = [
  { end: 500, suffix: "+", label: "Empresas ativas", decimals: 0 },
  { end: 25, suffix: "%", label: "Redução de custos", decimals: 0 },
  { end: 99.9, suffix: "%", label: "Uptime garantido", decimals: 1 },
  { end: 24, suffix: "/7", label: "Suporte dedicado", decimals: 0 },
];

const heroWords = [
  "Inteligente",
  "Eficiente",
  "Integrada",
  "Escalável",
  "Completa",
  "Poderosa",
];

const pricingPlans = {
  essencial: { monthly: "R$ 159,80", annual: "R$ 129,80" },
  standard: { monthly: "R$ 199,80", annual: "R$ 169,80" },
  premium: { monthly: "R$ 256,90", annual: "R$ 218,90" },
} as const;

type BillingCycle = "monthly" | "annual";

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useGSAP(() => {
    if (!heroRef.current) return;

    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: reduce)", () => {
      const hero = heroRef.current!;
      gsap.set(
        hero.querySelectorAll(
          "[data-hero-badge], [data-hero-subtitle], [data-hero-cta], [data-hero-trust], [data-hero-accent], .hero-art"
        ),
        { opacity: 1, autoAlpha: 1, x: 0, y: 0, scale: 1 }
      );
      const textEl = hero.querySelector<HTMLElement>("[data-hero-text]");
      if (textEl) gsap.set(textEl, { autoAlpha: 1 });
      gsap.set(hero.querySelectorAll<HTMLElement>("[data-hero-word]"), { autoAlpha: 0 });
      const firstWord = hero.querySelector<HTMLElement>("[data-hero-word]");
      if (firstWord) gsap.set(firstWord, { autoAlpha: 1 });
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const hero = heroRef.current!;
      const EASE = "power3.out";
      const DUR = { quick: 0.35, standard: 0.5, slow: 0.7 };

      const badge = hero.querySelector("[data-hero-badge]");
      const heroTextEl = hero.querySelector("[data-hero-text]");
      const heroAccentEl = hero.querySelector("[data-hero-accent]");
      const subtitleEl = hero.querySelector("[data-hero-subtitle]");
      const ctaGroup = hero.querySelector("[data-hero-cta]");
      const trust = hero.querySelector("[data-hero-trust]");
      const art = hero.querySelector<HTMLElement>(".hero-art");
      const artVisual = hero.querySelector<HTMLElement>(".hero-art-visual");
      const artTop = hero.querySelector<HTMLElement>(".hero-art-visual-top");
      const artBottom = hero.querySelector<HTMLElement>(".hero-art-visual-bottom");
      const ringLarge = hero.querySelector<HTMLElement>(".hero-art-ring-large");
      const ringSmall = hero.querySelector<HTMLElement>(".hero-art-ring-small");

      let splitText: SplitText | null = null;
      const splitAccents: SplitText[] = [];
      let splitSubtitle: SplitText | null = null;

      if (heroTextEl) {
        splitText = SplitText.create(heroTextEl, { type: "chars", tag: "span" });
        gsap.set(splitText.chars, { display: "inline-block", autoAlpha: 0, y: 40 });
      }

      if (heroAccentEl) {
        const accentWords = Array.from(
          heroAccentEl.querySelectorAll<HTMLElement>("[data-hero-word]")
        );
        accentWords.forEach((word) => {
          const split = SplitText.create(word, { type: "chars", tag: "span" });
          gsap.set(word, { autoAlpha: 0 });
          gsap.set(split.chars, { display: "inline-block", autoAlpha: 0, y: 40 });
          splitAccents.push(split);
        });
      }

      if (subtitleEl) {
        splitSubtitle = SplitText.create(subtitleEl, { type: "words", tag: "span" });
        gsap.set(splitSubtitle.words, { display: "inline-block", autoAlpha: 0, y: 24 });
      }

      const accentWords = heroAccentEl
        ? Array.from(heroAccentEl.querySelectorAll<HTMLElement>("[data-hero-word]"))
        : [];

      const accentLoop = gsap.timeline({ paused: true, repeat: -1, repeatDelay: 0.25 });
      splitAccents.forEach((split, index) => {
        const word = accentWords[index];
        accentLoop
          .set(word, { autoAlpha: 1 })
          .set(split.chars, { autoAlpha: 0, y: 40 })
          .to(split.chars, {
            autoAlpha: 1,
            y: 0,
            duration: 0.45,
            stagger: { each: 0.024, from: "start" },
            ease: "power3.out",
          })
          .to({}, { duration: 1.2 })
          .to(split.chars, {
            autoAlpha: 0,
            y: -22,
            duration: 0.32,
            stagger: { each: 0.016, from: "start" },
            ease: "power2.in",
          })
          .set(word, { autoAlpha: 0 });
      });

      const intro = gsap.timeline({ defaults: { ease: EASE } });

      if (art) {
        intro
          .fromTo(
            art,
            { opacity: 0, x: 48, scale: 0.97 },
            { opacity: 1, x: 0, scale: 1, duration: DUR.slow },
            0.05
          )
          .fromTo(ringLarge, { autoAlpha: 0 }, { autoAlpha: 1, duration: DUR.standard }, 0.2)
          .fromTo(ringSmall, { autoAlpha: 0 }, { autoAlpha: 1, duration: DUR.standard }, 0.3)
          .fromTo(artVisual, { autoAlpha: 0 }, { autoAlpha: 1, duration: DUR.standard }, 0.4)
          .fromTo(
            artTop,
            { autoAlpha: 0, y: 8 },
            { autoAlpha: 1, y: 0, duration: DUR.quick },
            0.6
          )
          .fromTo(
            artBottom,
            { autoAlpha: 0, y: 12 },
            { autoAlpha: 1, y: 0, duration: DUR.quick },
            0.72
          );
      }

      intro
        .fromTo(
          badge,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: DUR.quick },
          0
        )
        .to(
          splitText?.chars || [],
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.45,
            stagger: { each: 0.024, from: "start" },
          },
          0.15
        )
        .call(() => accentLoop.restart(), undefined, 0.55)
        .to(
          splitSubtitle?.words || [],
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.4,
            stagger: { each: 0.02, from: "start" },
          },
          0.7
        )
        .fromTo(
          ctaGroup,
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: DUR.standard },
          0.85
        )
        .fromTo(
          trust,
          { opacity: 0, y: 14 },
          { opacity: 1, y: 0, duration: DUR.standard },
          0.95
        );

      return () => {
        accentLoop.kill();
        splitText?.revert();
        splitAccents.forEach((split) => split.revert());
        splitSubtitle?.revert();
      };
    });

    return () => mm.revert();
  }, { scope: heroRef });

  return (
    <div className="min-h-screen bg-[#0C0B0A]">
      <SEO
        title="Plataforma Comercial Inteligente"
        description="A revenda do ZWeb nunca foi tão eficiente. Gerencie leads, propostas, negociações e implantações em uma única plataforma. Reduza custos em até 25%."
        keywords={["erp", "zweb", "gestão empresarial", "software de gestão", "revenda erp"]}
        canonicalUrl="/"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            getOrganizationSchema(),
            getProductSchema(),
            getFAQSchema(faqs),
          ],
        }}
      />

      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-dark-900/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <nav className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500 to-amber-500 font-display text-lg font-bold text-white shadow-lg shadow-accent-500/25">
              Z
            </div>
            <span className="font-display text-xl font-bold tracking-[-0.02em] text-white">ZWeb</span>
          </div>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#recursos" className="nav-link">Recursos</a>
            <a href="#beneficios" className="nav-link">Benefícios</a>
            <a href="#precos" className="nav-link">Preços</a>
            <a href="#faq" className="nav-link">FAQ</a>
            <a href="#contato" className="nav-link">Contato</a>
          </div>

          <div className="hidden items-center gap-4 md:flex">
            <a href="/login" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:border-white/25 hover:bg-white/5">
              Entrar
            </a>
            <a href="/solicitar-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-accent-500 to-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent-500/30 transition-all duration-300 hover:from-accent-400 hover:to-amber-400 hover:shadow-xl hover:shadow-accent-500/50 hover:-translate-y-0.5 active:translate-y-0">
              Solicitar Demo
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? "max-h-96" : "max-h-0"
          }`}
        >
          <div className="container border-t border-white/10 bg-dark-900/95 backdrop-blur-md px-4 py-4">
            <div className="flex flex-col gap-2">
              <a href="#recursos" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Recursos</a>
              <a href="#beneficios" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Benefícios</a>
              <a href="#precos" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Preços</a>
              <a href="#faq" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="#contato" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Contato</a>
              <div className="mt-2 flex flex-col gap-2 border-t border-white/10 pt-4">
                <a href="/login" className="btn-ghost justify-center" onClick={() => setMobileMenuOpen(false)}>Entrar</a>
                <a href="/solicitar-demo" className="btn-primary justify-center" onClick={() => setMobileMenuOpen(false)}>
                  Solicitar Demo
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section ref={heroRef} className="hero-surface relative overflow-hidden">
        {/* Editorial technology background */}
        <div className="hero-visual" aria-hidden="true">
          <div className="hero-grid" />
          <div className="hero-streak hero-streak-orange" />
          <div className="hero-streak hero-streak-gold" />
          <div className="hero-streak hero-streak-teal" />
          <div className="hero-streak hero-streak-coral" />
          <div className="hero-glow hero-glow-primary" />
          <div className="hero-glow hero-glow-secondary" />
          <div className="hero-orbit hero-orbit-wide" />
          <div className="hero-orbit hero-orbit-small" />
        </div>

        <div className="container relative z-10 py-16 md:py-24 lg:py-28">
          <div className="hero-layout">
            <div className="hero-copy">
              <div data-hero-badge className="mb-6 inline-flex">
                <span className="badge">
                  <Zap className="h-3 w-3" />
                  ERP completo para crescer com clareza
                </span>
              </div>

              <h1
                data-hero-heading
                className="max-w-3xl font-display text-4xl font-extrabold tracking-[-0.04em] text-white sm:text-5xl md:text-6xl lg:text-7xl"
              >
                <span data-hero-text className="inline-block">Operação</span>{" "}
                <span
                  data-hero-accent
                  aria-live="polite"
                  className="hero-word-rotator text-left"
                >
                  {heroWords.map((word) => (
                    <span
                      key={word}
                      data-hero-word
                      className="gradient-text-ai glow-text-ai"
                    >
                      {word}
                    </span>
                  ))}
                </span>
              </h1>

              <p
                data-hero-subtitle
                className="mt-6 max-w-xl text-lg text-ink-200 md:text-xl leading-relaxed"
              >
                O ZWeb conecta vendas, financeiro e atendimento em uma única plataforma
                para você decidir melhor e avançar mais rápido.
              </p>

              <div
                data-hero-cta
                className="mt-10 flex flex-col items-start gap-4 sm:flex-row"
              >
                <a href="/solicitar-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-accent-500 to-amber-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-accent-500/30 transition-all duration-300 hover:from-accent-400 hover:to-amber-400 hover:shadow-xl hover:shadow-accent-500/50 hover:-translate-y-0.5 active:translate-y-0">
                  Solicitar demonstração gratuita
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a href="#recursos" className="hero-secondary-link">
                  Explorar recursos
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div
                data-hero-trust
                className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink-300"
              >
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span>14 dias grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span>Sem cartão de crédito</span>
                </div>
              </div>
            </div>

            <div className="hero-art" aria-hidden="true">
              <div className="hero-art-bokeh hero-art-bokeh-pink" />
              <div className="hero-art-bokeh hero-art-bokeh-blue" />
              <div className="hero-art-ring hero-art-ring-large" />
              <div className="hero-art-ring hero-art-ring-small" />

              <div className="hero-art-visual">
                <div className="hero-art-visual-content">
                  <div className="hero-art-visual-top">
                    <div className="hero-art-visual-brand">ZWEB / OPS</div>
                    <span className="hero-art-visual-chip">Realtime</span>
                  </div>

                  <div className="hero-art-visual-stage">
                    <div className="hero-art-visual-lines" />
                    <div className="hero-art-visual-device" />
                    <div className="hero-art-visual-panel hero-art-visual-panel-top" />
                    <div className="hero-art-visual-panel hero-art-visual-panel-left" />
                    <div className="hero-art-visual-panel hero-art-visual-panel-right" />
                    <div className="hero-art-visual-panel hero-art-visual-panel-bottom" />
                  </div>

                  <div className="hero-art-visual-bottom">
                    <div className="hero-art-visual-dots"><span /><span /><span /></div>
                    <span className="hero-art-visual-status">ONLINE</span>
                  </div>
                </div>
              </div>

              <div className="hero-system-card hero-system-card-left">
                <div className="hero-system-card-icon">
                  <Wallet className="h-4 w-4" />
                </div>
                <div>
                  <span className="hero-system-card-label">Faturamento hoje</span>
                  <strong>R$ 42.350,00</strong>
                </div>
                <span className="hero-status-line" />
              </div>

              <div className="hero-system-card hero-system-card-right">
                <div className="hero-system-card-icon hero-system-card-icon-violet">
                  <Users className="h-4 w-4" />
                </div>
                <div>
                  <span className="hero-system-card-label">Pedidos abertos</span>
                  <strong>38 em andamento</strong>
                </div>
                <span className="hero-status-dot" />
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0C0B0A"/>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
            {stats.map((stat, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="stat-card">
                  <div className="font-display text-3xl font-bold text-accent-500 md:text-4xl">
                    <CountUp end={stat.end} suffix={stat.suffix} decimals={stat.decimals} />
                  </div>
                  <div className="mt-2 text-sm text-ink-400">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="recursos" className="section section-subtle">
        <div className="container">
          <ScrollReveal>
            <div className="text-center">
              <span className="badge">Recursos</span>
              <SectionHeading className="mt-4">
                Tudo que você precisa
              </SectionHeading>
              <p className="section-subtitle mx-auto max-w-2xl">
                Recursos completos para operar sua empresa com eficiência, independentemente do plano contratado.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="card feature-card group h-full">
                  <div className="icon-wrapper">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-white">{feature.title}</h3>
                  <p className="mt-2 text-ink-400">{feature.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="section">
        <div className="container">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <ScrollReveal>
              <div>
                <span className="badge">Benefícios</span>
                <SectionHeading className="mt-4">
                  Por que escolher o ZWeb?
                </SectionHeading>
                <p className="section-subtitle mt-4">
                  Transforme sua operação com uma plataforma completa e eficiente.
                </p>
                <ul className="mt-8 space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-4">
                       <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-accent-500/10 text-accent-400">
                        <benefit.icon className="h-5 w-5" />
                      </div>
                      <span className="pt-2 text-ink-200">{benefit.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-accent-500/20 to-amber-500/10 blur-2xl" />
                <div className="benefit-showcase relative rounded-3xl glass-strong p-8 text-white shadow-2xl md:p-12 glow-box">
                  <div className="text-center">
                    <div className="font-display text-6xl font-bold md:text-7xl gradient-text">25%</div>
                    <div className="mt-4 text-xl font-medium text-white/90">Redução de custos</div>
                    <div className="mt-4 text-white/60">
                      Em média, nossos clientes reduzem custos operacionais em até 25%
                    </div>
                    <div className="mt-8 flex items-center justify-center gap-2 text-amber-400">
                      <TrendingUp className="h-5 w-5" />
                      <span className="text-sm font-medium">+40% produtividade</span>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="section section-subtle">
        <div className="container">
          <ScrollReveal>
            <div className="text-center">
              <span className="badge">Preços</span>
              <SectionHeading className="mt-4">
                Planos para cada necessidade
              </SectionHeading>
              <p className="section-subtitle mx-auto max-w-2xl">
                Escolha o plano ideal para sua empresa. Todos incluem 14 dias de teste grátis.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="mt-10 flex justify-center">
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-sm">
                <button
                  onClick={() => setBilling("monthly")}
                  className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    billing === "monthly"
                      ? "bg-white text-ink-900 shadow-lg shadow-white/10"
                      : "text-ink-400 hover:text-white"
                  }`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setBilling("annual")}
                  className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    billing === "annual"
                      ? "bg-white text-ink-900 shadow-lg shadow-white/10"
                      : "text-ink-400 hover:text-white"
                  }`}
                >
                  Anual
                </button>
              </div>
            </div>
          </ScrollReveal>

          <div className="mt-16 grid gap-6 md:grid-cols-3 lg:gap-8">
            {/* Essencial */}
            <ScrollReveal delay={0}>
              <div className="card pricing-card h-full flex flex-col">
                <div className="mb-6">
                  <div className="pricing-plan-badge">Essencial</div>
                  <div className="mt-5 flex items-center gap-3 text-lg text-ink-100">
                    <User className="h-7 w-7 text-accent-400" />
                    <span>1 usuário</span>
                  </div>
                </div>
                <div className="pricing-divider" />
                <ul className="flex-1 space-y-3 text-ink-300">
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Cadastro de Clientes</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Cadastro de Produtos</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Cadastro de Fornecedores</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Emissão de Nota Fiscal</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Emissão de Orçamentos</span>
                  </li>
                </ul>
                <div className="mt-8 mb-6">
                  <div className="pricing-kicker">A partir de</div>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="font-display text-4xl font-bold text-white">{pricingPlans.essencial[billing]}</span>
                    <span className="pb-1 text-ink-400">/mês</span>
                  </div>
                </div>
                <button className="btn-secondary w-full">
                  Começar Agora
                </button>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={100}>
              <div className="card-featured pricing-card pricing-card-featured h-full flex flex-col">
                <div className="pricing-highlight">Recomendado</div>
                <div className="mb-6">
                  <div className="pricing-plan-badge">Standard</div>
                  <div className="mt-5 flex items-center gap-3 text-lg text-ink-100">
                    <User className="h-7 w-7 text-accent-400" />
                    <span>3 usuários</span>
                  </div>
                </div>
                <div className="pricing-divider" />
                <ul className="flex-1 space-y-3 text-ink-300">
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span className="font-semibold text-white">Tudo do Plano Essencial +</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Frente de Caixa PDV</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Controle de Contas e Saldos</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Emissão de Boletos</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet">•</span>
                    <span>Emissão de MDF-e</span>
                  </li>
                </ul>
                <div className="mt-8 mb-6">
                  <div className="pricing-kicker">Melhor custo-benefício</div>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="font-display text-4xl font-bold text-white">{pricingPlans.standard[billing]}</span>
                    <span className="pb-1 text-ink-400">/mês</span>
                  </div>
                </div>
                <button className="btn-primary w-full">
                  Começar Agora
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </ScrollReveal>

            {/* Premium */}
            <ScrollReveal delay={200}>
              <div className="card pricing-card pricing-card-premium h-full flex flex-col">
                <div className="mb-6">
                  <div className="pricing-plan-badge pricing-plan-badge-premium">Premium</div>
                  <div className="mt-5 flex items-center gap-3 text-lg text-ink-100">
                    <Users className="h-7 w-7 text-amber-400" />
                    <span>Usuários ilimitados</span>
                  </div>
                </div>
                <div className="pricing-divider" />
                <ul className="flex-1 space-y-3 text-ink-300">
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span className="font-semibold text-white">Tudo do Plano Standard +</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span>SPED e Sintegra</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span>Tabelas de Preço</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span>Retaguarda Offline</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span>Ordens de Serviço</span>
                  </li>
                  <li className="pricing-list-item">
                    <span className="pricing-bullet pricing-bullet-premium">•</span>
                    <span>Cadastro de Grades</span>
                  </li>
                </ul>
                <div className="mt-8 mb-6">
                  <div className="pricing-kicker">Operação completa</div>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="font-display text-4xl font-bold text-white">{pricingPlans.premium[billing]}</span>
                    <span className="pb-1 text-ink-400">/mês</span>
                  </div>
                </div>
                <button className="btn-secondary w-full">
                  Fale Conosco
                </button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section">
        <div className="container">
          <ScrollReveal>
            <div className="text-center">
              <span className="badge">Depoimentos</span>
              <SectionHeading className="mt-4">
                O que dizem nossos clientes
              </SectionHeading>
              <p className="section-subtitle mx-auto max-w-2xl">
                Veja como o ZWeb está transformando empresas como a sua.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="card h-full">
                  <div className="flex gap-1">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="mt-4 text-ink-300 italic">"{testimonial.content}"</p>
                  <div className="mt-6 flex items-center gap-4 border-t border-white/10 pt-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-accent-500 to-amber-500 font-semibold text-white">
                      {testimonial.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-ink-400">
                        {testimonial.role} • {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section section-subtle">
        <div className="container mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="text-center">
              <span className="badge">FAQ</span>
              <SectionHeading className="mt-4">
                Perguntas Frequentes
              </SectionHeading>
              <p className="section-subtitle">
                Tire suas dúvidas sobre o ZWeb.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <div className="card overflow-hidden">
                  <button
                    className="flex w-full items-center justify-between p-6 text-left"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="pr-4 font-semibold text-white">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-ink-400 transition-transform duration-200 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? "max-h-48" : "max-h-0"
                    }`}
                  >
                    <div className="px-6 pb-6 text-ink-300">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contato" className="section cta-section relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-accent-500/15 blur-3xl animate-glow-pulse" />
          <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-amber-500/10 blur-3xl animate-glow-pulse" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
        </div>

        <div className="container relative">
          <ScrollReveal>
            <div className="mx-auto max-w-3xl text-center">
              <SectionHeading className="text-3xl font-bold text-white sm:text-4xl md:text-5xl">
                Pronto para transformar sua operação?
              </SectionHeading>
              <p className="mt-6 text-lg text-ink-200">
                Solicite uma demonstração gratuita e veja como o ZWeb pode
                transformar sua empresa.
              </p>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href="/solicitar-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent-500 to-amber-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-accent-500/30 transition-all duration-300 hover:from-accent-400 hover:to-amber-400 hover:shadow-xl hover:shadow-accent-500/50 hover:-translate-y-0.5 active:translate-y-0"
                >
                  Solicitar Demonstração Gratuita
                  <ArrowRight className="h-5 w-5" />
                </a>
                <a
                  href="https://wa.me/5511999999999"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/30"
                >
                  <MessageSquare className="h-5 w-5" />
                  Falar no WhatsApp
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-surface">
        <div className="container py-12 md:py-16">
          <div className="footer-grid">
              <div className="footer-brand">
                <div className="flex items-center gap-2.5">
                  <div className="footer-brand-mark">Z</div>
                  <span className="font-display text-xl font-bold tracking-[-0.02em] text-white">ZWeb</span>
                </div>
              <p className="mt-4 max-w-sm text-surface-400">
                Plataforma Comercial Inteligente para Revenda ZWeb. Gerencie sua operação de forma eficiente e escalável.
              </p>
              <div className="mt-6 flex gap-4">
                <a href="#" className="footer-social-link" aria-label="Instagram">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" className="footer-social-link" aria-label="YouTube">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>

            <div className="footer-column">
              <h4>Produto</h4>
              <ul>
                <li><a href="#recursos">Recursos</a></li>
                <li><a href="#precos">Preços</a></li>
                <li><a href="/blog">Blog</a></li>
                <li><a href="/faq">FAQ</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Conteúdo</h4>
              <ul>
                <li><a href="/tutoriais">Tutoriais</a></li>
                <li><a href="/casos-de-sucesso">Casos de Sucesso</a></li>
                <li><a href="/comparativos">Comparativos</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Contato</h4>
              <ul>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>contato@zweb.com.br</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>(11) 99999-9999</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>São Paulo, SP</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} ZWeb. Todos os direitos reservados.</p>
            <div className="flex gap-6 text-sm">
              <a href="/privacidade">Política de Privacidade</a>
              <a href="/termos">Termos de Uso</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
